<template>
    <div>
        <v-overlay :value="showLoader">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
        <div class="pa-5">
            <v-row no-gutters>
                <v-col class="col-item">
                    <date-picker
                        v-model="datepickerStartDate"
                        :max-date="dateToday"
                        :min-date="minDate"
                        :popover="{ placement: 'bottom', visibility: 'click' }"
                    >
                        <template v-slot="{ inputValue, inputEvents }">
                            <v-text-field
                                id="start_date"
                                v-on="inputEvents"
                                :value="inputValue"
                                append-icon="fas fa-calendar"
                                :label="'Turnover start date' | t"
                                hide-details
                                dense
                                outlined
                            />
                        </template>
                    </date-picker>
                </v-col>
                <v-col class="col-item ml-4">
                    <date-picker
                        v-model="datepickerEndDate"
                        :max-date="dateToday"
                        :min-date="minDate"
                        :popover="{ placement: 'bottom', visibility: 'click' }"
                    >
                        <template v-slot="{ inputValue, inputEvents }">
                            <v-text-field
                                id="end_date"
                                v-on="inputEvents"
                                append-icon="fas fa-calendar"
                                :label="'Turnover end date' | t"
                                :value="inputValue"
                                hide-details
                                dense
                                outlined
                            />
                        </template>
                    </date-picker>
                </v-col>
            </v-row>
        </div>
        <google-map-loader :mapConfig="mapConfig" :apiKey="apiKey" />
    </div>
</template>

<script>
import Store from "../../store";
import MarkerClusterer from "@google/markerclusterer";
import { mapSettings } from "@/constants/mapSettings";
import { mapMutations, mapState } from "vuex";
import GoogleMapLoader from "./google-maps-loader";
import settings from "../../settings";
import DatePicker from "v-calendar/lib/components/date-picker.umd";

export default {
    name: "shops-map-view.vue",
    components: {
        GoogleMapLoader,
        DatePicker
    },
    data() {
        return {
            markers: [],
            markerCluster: null,
            contentString: null,
            apiKey: settings.google_maps_api_key,
            dateToday: new Date(),
            minDate: new Date(2020, 0, 1),
            datepickerStartDate: new Date(2020, 0, 1),
            datepickerEndDate: new Date()
        };
    },
    computed: {
        mapConfig() {
            return {
                ...mapSettings,
                center: this.mapCenter
            };
        },
        mapCenter() {
            return { lat: 58.865278, lng: 25.560598 };
        },
        ...mapState("ShopsMapStore", [
            "shops",
            "map",
            "google",
            "showLoader",
            "searchShopString",
            "startDate",
            "endDate"
        ])
    },
    mounted() {
        Store.dispatch("ShopsMapStore/getShopsCoordinates", { term: this.searchShopString });
    },

    watch: {
        shops: async function() {
            await Store.commit("ShopsMapStore/startLoader");
            if (this.markerCluster) {
                this.markerCluster.clearMarkers();
            }
            this.markers = [];
            for (let i = 0; i < this.shops.length; i++) {
                let data = {
                    id: this.shops[i].id,
                    position: { lat: parseFloat(this.shops[i].lat), lng: parseFloat(this.shops[i].lng) },
                    infoText: this.setInfoText(
                        this.shops[i].name,
                        this.shops[i].turnover,
                        this.shops[i].address,
                        this.shops[i].address_place,
                        this.shops[i].address_parish,
                        this.shops[i].address_county
                    ),
                    icon: this.getMarkerIcon(this.shops[i].name, this.shops[i].type, this.shops[i].image_path),
                    map: this.map
                };
                this.markers.push(data);
            }
            let map = this.map;
            let google = this.google;
            let markerClickHandler = marker => {
                map.setZoom(18);
                map.setCenter(marker.getPosition());
            };
            setTimeout(() => {
                let infoWindow;
                try {
                    infoWindow = new google.maps.InfoWindow({});
                } catch (e) {
                    console.log(e);
                }
                this.markers = this.markers.map(location => {
                    let marker;
                    try {
                        marker = new google.maps.Marker({ ...location });
                        marker.addListener("click", () => markerClickHandler(marker));
                        marker.addListener("mouseover", function() {
                            infoWindow.setContent(marker.infoText);
                            infoWindow.open(map, marker);
                        });
                        marker.addListener("mouseout", function() {
                            infoWindow.close(map, marker);
                        });
                    } catch (e) {
                        console.log(e);
                    }
                    return marker;
                });
                let clusterStyles = [
                    {
                        textColor: "red",
                        url: require("../../assets/images/maps/m1.png"),
                        height: 52,
                        width: 53
                    },
                    {
                        textColor: "red",
                        url: require("../../assets/images/maps/m2.png"),
                        height: 55,
                        width: 56
                    },
                    {
                        textColor: "red",
                        url: require("../../assets/images/maps/m3.png"),
                        height: 65,
                        width: 66
                    }
                ];
                let mcOptions = {
                    gridSize: 70,
                    styles: clusterStyles,
                    maxZoom: 20
                };
                this.markerCluster = new MarkerClusterer(this.map, this.markers, mcOptions);
                Store.commit("ShopsMapStore/stopLoader");
            }, 500);
        },
        datepickerStartDate: function(value) {
            Store.commit("ShopsMapStore/setStartDate", value);
        },
        datepickerEndDate: function(value) {
            Store.commit("ShopsMapStore/setEndDate", value);
        }
    },
    methods: {
        ...mapMutations("ShopsMapStore", ["startLoader", "stopLoader"]),
        setInfoText(name, turnover, street, place, parish, county) {
            let infoDiv = `<div id="content"><h1 id="firstHeading" class="firstHeading">${name}</h1><div id="bodyContent">`;
            if (turnover) infoDiv += `<h3><strong>${this.t("Turnover")}:</strong> ${this.rn(turnover)} €</h3>`;
            if (street) infoDiv += `<i>${street}</i><br/>`;
            if (place) infoDiv += `<i>${place}</i><br/>`;
            if (parish) infoDiv += `<i>${parish}</i><br/>`;
            if (county) infoDiv += `<i>${county}</i>`;
            infoDiv += "</div></div>";
            return infoDiv;
        },
        getMarkerIcon(name, type, image) {
            if (image) {
                return image;
            } else if (/circle k/gi.test(name)) {
                return require("../../assets/images/maps/circle_k.png");
            } else if (/selver/gi.test(name)) {
                return require("../../assets/images/maps/selver.png");
            } else if (/maxima/gi.test(name)) {
                return require("../../assets/images/maps/maxima.png");
            } else if (/comarket/gi.test(name)) {
                return require("../../assets/images/maps/comarket.png");
            } else if (/aldar/gi.test(name)) {
                return require("../../assets/images/maps/aldar.png");
            } else if (/rimi/gi.test(name)) {
                return require("../../assets/images/maps/rimi.png");
            } else if (/prisma/gi.test(name)) {
                return require("../../assets/images/maps/prisma.png");
            } else if (/kaupmees/gi.test(name)) {
                return require("../../assets/images/maps/kaupmees.png");
            } else if (/maksimarket/gi.test(name) || /konsum/gi.test(name) || /coop/gi.test(type)) {
                return require("../../assets/images/maps/coop.png");
            } else if (/stockmann/gi.test(name)) {
                return require("../../assets/images/maps/stockmann.png");
            } else if (/a1000/gi.test(name)) {
                return require("../../assets/images/maps/a1000.png");
            } else if (/\b(A+)\b/gi.test(type) || /\b(B+)\b/gi.test(type) || /\b(C+)\b/gi.test(type)) {
                return require("../../assets/images/maps/grossi_toidukaubad.png");
            } else if (/tankla/gi.test(type)) {
                return require("../../assets/images/maps/gas.png");
            } else {
                return require("../../assets/images/maps/basket.png");
            }
        }
    }
};
</script>

<style scoped></style>
